import { createContext, useCallback, useContext, useMemo, useReducer, useRef, useState, type Dispatch, type ReactNode } from "react";
import type { TemplateData } from "./types";
import {
  templateBuilderReducer,
  type TemplateBuilderCommand,
  type TemplateBuilderState,
} from "./state";
import { buildTemplatePayload, validateTemplate } from "./serialization";

type TemplateBuilderContextValue = TemplateBuilderState & {
  dispatch: Dispatch<TemplateBuilderCommand>;
  puckRevision: number;
  replacePuckData: (data: TemplateData, revision: number) => void;
  validationIssues: string[];
  payload: ReturnType<typeof buildTemplatePayload>;
};

const TemplateBuilderContext = createContext<TemplateBuilderContextValue | undefined>(undefined);

export const TemplateBuilderProvider = ({
  initialState,
  children,
}: {
  initialState: TemplateBuilderState;
  children: ReactNode;
}) => {
  const [state, reducerDispatch] = useReducer(templateBuilderReducer, initialState);
  const [puckRevision, setPuckRevision] = useState(0);
  const puckRevisionRef = useRef(0);
  // Puck owns its editing store after mount. Rename/move alter bindings outside
  // that store, so remount only for those structural external replacements.
  const dispatch = useCallback((command: TemplateBuilderCommand) => {
    reducerDispatch(command);
    if (command.type === "renameInput" || command.type === "replaceState") {
      // Advance synchronously so an onChange queued by the Puck instance being
      // replaced cannot restore its stale bindings before React remounts it.
      puckRevisionRef.current += 1;
      setPuckRevision((revision) => revision + 1);
    }
  }, []);
  const replacePuckData = useCallback((data: TemplateData, revision: number) => {
    if (revision === puckRevisionRef.current) reducerDispatch({ type: "replaceData", data });
  }, []);
  const value = useMemo(() => ({
    ...state,
    dispatch,
    puckRevision,
    replacePuckData,
    validationIssues: validateTemplate(state.data, state.inputDefinitions),
    payload: buildTemplatePayload(state.data, state.inputDefinitions),
  }), [state, dispatch, puckRevision, replacePuckData]);
  return <TemplateBuilderContext.Provider value={value}>{children}</TemplateBuilderContext.Provider>;
};

export const useTemplateBuilder = () => {
  const value = useContext(TemplateBuilderContext);
  if (!value) throw new Error("useTemplateBuilder must be used inside TemplateBuilderProvider.");
  return value;
};

export const useTemplateDataChange = () => {
  const { puckRevision, replacePuckData } = useTemplateBuilder();
  return useCallback((data: TemplateData) => replacePuckData(data, puckRevision), [puckRevision, replacePuckData]);
};
